import { useRef, useEffect, useState, useMemo } from "react";
import gsap from "gsap";

export default function SplitText({
  text,
  className = "",
  delay = 50,
  duration = 0.8,
  ease = "power3.out",
  splitType = "chars",
  from = { opacity: 0, y: 40 },
  to = { opacity: 1, y: 0 },
  threshold = 0.1,
  textAlign = "center",
  tag: Tag = "p",
  onAnimationComplete,
}) {
  const containerRef = useRef(null);
  const [inView, setInView] = useState(false);
  const hasAnimated = useRef(false);

  const elements = useMemo(() => {
    if (splitType === "words") {
      return text.split(/(\s+)/).map((word, i) => ({
        key: i,
        content: word,
        isSpace: /^\s+$/.test(word),
      }));
    }
    // chars
    return text.split("").map((char, i) => ({
      key: i,
      content: char,
      isSpace: char === " ",
    }));
  }, [text, splitType]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          setInView(true);
          hasAnimated.current = true;
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  useEffect(() => {
    if (!inView || !containerRef.current) return;
    const targets = containerRef.current.querySelectorAll(".split-char");
    gsap.set(targets, from);
    gsap.to(targets, {
      ...to,
      duration,
      ease,
      stagger: delay / 1000,
      onComplete: onAnimationComplete,
    });
  }, [inView, from, to, duration, ease, delay, onAnimationComplete]);

  return (
    <Tag
      ref={containerRef}
      className={className}
      style={{ textAlign, display: "inline-block", overflow: "hidden" }}
    >
      {elements.map((el) =>
        el.isSpace ? (
          <span key={el.key}>&nbsp;</span>
        ) : (
          <span
            key={el.key}
            className="split-char"
            style={{ display: "inline-block", opacity: 0 }}
          >
            {el.content}
          </span>
        )
      )}
    </Tag>
  );
}
